import { useEffect, useState } from "react";
import { Copy, Loader2, Mail, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const STATUS = { sent: "Reference request sent", queued: "Reference request queued", failed: "Reference request failed" };

export const RefReqChip = ({ r, testid }) => r ? (
  <span data-testid={testid} title={r.error || ""} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${r.status === "failed" ? "bg-rose-50 text-rose-700" : r.status === "queued" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
    {STATUS[r.status] || r.status}{r.at ? ` · ${r.at.slice(0, 10)}` : ""}
  </span>) : null;

export const TemplateText = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/suppliers/ref-requests/template").then((r) => setT(r.data)); }, []);
  if (!t) return null;
  const copy = (k) => navigator.clipboard.writeText(t[k]).then(() => toast.success(`${k.toUpperCase()} template copied`));
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="refreq-template">
      {["en", "fr"].map((k) => (
        <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-1 flex items-center justify-between"><b className="text-xs uppercase tracking-wider text-slate-500">{k === "en" ? "English" : "Français"}</b>
            <button className="flex items-center gap-1 text-xs text-[#D52B3B]" data-testid={`refreq-template-copy-${k}`} onClick={() => copy(k)}><Copy className="h-3.5 w-3.5" />Copy</button></div>
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap text-xs text-slate-700" data-testid={`refreq-template-${k}`}>{t[k]}</pre>
        </div>))}
    </div>
  );
};

export const RefReqBatch = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setD(null); api.get("/suppliers/ref-requests/preview", { params: { lang } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); } }, [open, lang]);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post("/suppliers/ref-requests", {}); toast.success(`${r.data.queued} reference requests queued`); setOpen(false); onDone?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const retry = async () => {
    try { const r = await api.post("/suppliers/ref-requests/retry-failed"); toast.success(`${r.data.queued} failed reference requests re-queued`); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" data-testid="refreq-batch-btn" onClick={() => setOpen(true)}><Mail className="mr-1.5 h-4 w-4" />Request mill references</Button>
      <Button variant="outline" data-testid="refreq-retry-btn" onClick={retry}><RotateCw className="mr-1.5 h-4 w-4" />Retry failed requests</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="refreq-dialog">
          <DialogHeader><DialogTitle>Request export-declaration EUDR references</DialogTitle></DialogHeader>
          {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-4 text-sm">
              <p data-testid="refreq-count"><b>{d.count}</b> EU-dispatch suppliers with a contact will receive the email. Not included: {d.excluded.intercompany} intercompany, {d.excluded.non_eu} non-EU dispatch, {d.no_contact.length} without a contact.</p>
              {d.no_contact.length > 0 && <details data-testid="refreq-no-contact"><summary className="cursor-pointer text-amber-700">No contact — not sent ({d.no_contact.length})</summary>
                <ul className="mt-1 max-h-32 overflow-auto text-xs">{d.no_contact.map((x) => <li key={x.id}>{x.vendor} · {x.name} ({x.mill_country})</li>)}</ul></details>}
              <div className="flex gap-2">{["en", "fr"].map((k) => <Button key={k} size="sm" variant={lang === k ? "default" : "outline"} data-testid={`refreq-lang-${k}`} onClick={() => setLang(k)}>{k.toUpperCase()}</Button>)}</div>
              {d.sample ? (
                <div data-testid="refreq-sample"><p className="text-xs text-slate-500">Preview for {d.sample.supplier} ({d.sample.to}) · {d.sample.subject}</p>
                  <iframe title="email preview" srcDoc={d.sample.html} className="mt-1 h-96 w-full rounded border" data-testid="refreq-preview-frame" /></div>
              ) : <p className="text-slate-500">No recipient to preview. The template text is below.</p>}
              <TemplateText />
            </div>)}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="refreq-confirm" disabled={busy || !d || !d.count} onClick={send}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Send to {d?.count || 0} suppliers</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const RefReqSingle = ({ s, onSent }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/suppliers/${s.id}/ref-request`); r.data.ok ? toast.success("Reference request sent") : toast.error(`Email failed: ${r.data.error}`); onSent?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button variant="outline" size="sm" data-testid="refreq-single-btn" disabled={busy} onClick={go}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}Request references</Button>;
};
